// @ts-strict-ignore
/* Not from the @lib/dust barrel: it re-exports modules that import @lib/fetch, which loads this file. */
import { getRealtimeTransport } from "@lib/dust/getRealtimeTransport";
import sharedConstants from "@lib/shared_constants";

import type { FetchMethod, FetchOptions, SettingsOptions } from "../types";
import { encodeParams } from "./encodeParams";
import { getClientOriginHeaders } from "./getClientOriginHeaders";
import { getToken } from "./getToken";

const isGet = (method: string) => method === "GET";

const { TRANSPORTS: transports, HEADER: transportHeader } = sharedConstants.REALTIME_TRANSPORT;

export const enhanceFetchArgs = (
  url: string,
  method: FetchMethod,
  params: Record<string, any> = {},
  options: FetchOptions = {},
  settings: SettingsOptions = { setContentType: true, returnDetailedError: false }
): [string, RequestInit] => {
  const headers = options && options.headers;
  const paramsIsEmpty = !Object.keys(params).length;

  const metatag: HTMLMetaElement = document.querySelector('meta[name="app-group-id"]');
  const appGroupId = metatag?.content;

  if ((isGet(method) && !paramsIsEmpty) || (options && options.queryParams)) {
    // ensure all URLs contain an explicit app group id to avoid cross-tab app group id pollution
    url = `${url}?${encodeParams(isGet(method) ? params : options.queryParams)}`;
    delete options.queryParams;
  }

  if (!url.includes(appGroupId)) {
    // ensure the current app group id is always explicitly added to the URL
    url = `${url}${url.includes("?") ? "&" : "?"}app_group_id=${appGroupId}`;
  }

  let body: string | FormData;
  if (isGet(method)) {
    body = undefined;
  } else {
    body = !paramsIsEmpty ? JSON.stringify(params) : options.body;
  }

  const fetchOptions: RequestInit = {
    credentials: "same-origin",
    ...options,
    method,
    body,
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getToken(),
      // Only async requests, which name the job whose result comes back over realtime. No
      // header means Pusher, which every tab listens on.
      ...(params.job_token && getRealtimeTransport() === transports.DUST
        ? { [transportHeader]: transports.DUST }
        : {}),
      ...getClientOriginHeaders(settings.clientOrigin),
      ...headers,
    },
  };

  if (!settings.fetchBlobType) {
    fetchOptions.headers["Accept"] = "application/json";
  }

  // Explicit check on false because if undefined we should default to true
  if (settings.setContentType !== false) {
    const contentType = headers && headers["Content-Type"];
    fetchOptions.headers["Content-Type"] = contentType || "application/json";
  }

  return [url, fetchOptions];
};
